import { useMemo, useState } from 'react';
import type { Control } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import { enableTraceBasedMetrics } from '@gonfalon/dogfood-flags/dogfood-flags';
import { LaunchDarklyLogoIcon } from '@gonfalon/icons/LaunchDarklyLogoIcon';
import { skeletonStyles } from '@gonfalon/metric-events/Sources';
import { DataSourceType } from '@gonfalon/metrics/constants';
import { isLDHosted } from '@gonfalon/metrics/isLDHosted';
import { MetricKind } from '@gonfalon/metrics/MetricKind';
import { getMetricDataSourcesQuery } from '@gonfalon/rest-api/queries/metricDataSource';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { Button, Label, Link, ListBox, ListBoxItem, Popover, Select, SelectValue } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';

import type { MetricForm } from './formUtils';
import { useWarehouseManifests } from './useWarehouseManifests';

import styles from './EventSourceField.module.css';

const TRACES_SELECTION = 'traces';

const LD_HOSTED_DEFAULT = {
  key: DataSourceType.LAUNCHDARKLY_HOSTED,
  name: 'LaunchDarkly hosted',
};

export const EventSourceField = ({
  control,
  onChange,
  isDisabled,
  disableLDHosted,
}: {
  control: Control<MetricForm>;
  onChange?: ({
    dataSourceKey,
    kind,
  }: {
    dataSourceKey: MetricForm['dataSourceKey'];
    kind: Extract<MetricKind, MetricKind.TRACE | MetricKind.CUSTOM>;
  }) => void;
  isDisabled?: boolean;
  disableLDHosted?: boolean;
}) => {
  const projectKey = useProjectKey();
  const dataSourceKeyFormValue = useWatch({ control, name: 'dataSourceKey' });
  const kindFormValue = useWatch({ control, name: 'kind' });
  const ratioOrSimpleFormValue = useWatch({ control, name: 'ratioOrSimple' });
  const { data } = useQuery(getMetricDataSourcesQuery({ projKey: projectKey }));
  const dataSources = useMemo(() => data?.items?.filter?.((ds) => Boolean(ds.environmentKey)), [data]);
  const warehouseManifests = useWarehouseManifests();
  const [lastSelectedValue, setLastSelectedValue] = useState<string | null>(null);

  const selectValue = useMemo(() => {
    if (kindFormValue === 'trace') {
      return TRACES_SELECTION;
    }

    if (isLDHosted(dataSourceKeyFormValue)) {
      return DataSourceType.LAUNCHDARKLY_HOSTED;
    }

    const selectedDataSource = dataSources?.find((ds) => ds.key === dataSourceKeyFormValue);
    const selectedWarehouse = warehouseManifests?.find(
      (manifest) => manifest?.key === selectedDataSource?.integrationKey,
    )?.key;

    return selectedWarehouse ?? lastSelectedValue ?? undefined;
  }, [dataSourceKeyFormValue, kindFormValue, dataSources, warehouseManifests, lastSelectedValue]);

  if (ratioOrSimpleFormValue === 'ratio') {
    return (
      <Select className={styles.eventSourceSelect} isDisabled>
        <Label>Define metric using</Label>
        <Button>
          <Icon name="package-outline" size="small" />
          Warehouse native
        </Button>
      </Select>
    );
  }

  return (
    <Select
      className={styles.eventSourceSelect}
      onChange={(value) => {
        setLastSelectedValue(String(value));
        switch (String(value)) {
          case DataSourceType.LAUNCHDARKLY_HOSTED:
            onChange?.({ dataSourceKey: LD_HOSTED_DEFAULT.key, kind: MetricKind.CUSTOM });
            break;
          case TRACES_SELECTION:
            onChange?.({ dataSourceKey: DataSourceType.LAUNCHDARKLY_HOSTED, kind: MetricKind.TRACE });
            break;
          default:
            if (typeof value === 'string') {
              const defaultDataSourceForWarehouse = dataSources?.find((ds) => ds.integrationKey === value);
              onChange?.({ dataSourceKey: defaultDataSourceForWarehouse?.key, kind: MetricKind.CUSTOM });
            }
            break;
        }
      }}
      value={selectValue ?? null}
      isDisabled={isDisabled}
    >
      <Label>Define metric using</Label>
      <Button>
        <SelectValue className={styles.selectedOption} />
        <Icon name="chevron-down" size="small" />
      </Button>
      {kindFormValue === 'trace' && (
        <Link
          href={getDocumentationUrl('home/metrics/create-trace-metrics#creating-trace-metrics')}
          target="_blank"
          className={styles.learnMore}
        >
          Learn more <Icon name="book-external-link" size="tiny" />
        </Link>
      )}
      <Popover>
        <ListBox disabledKeys={disableLDHosted ? [DataSourceType.LAUNCHDARKLY_HOSTED] : []}>
          <ListBoxItem id={DataSourceType.LAUNCHDARKLY_HOSTED} data-test-id="ld-data-source">
            <LaunchDarklyLogoIcon size="small" />
            {LD_HOSTED_DEFAULT.name}
          </ListBoxItem>
          {warehouseManifests?.map((manifest, i) => {
            const key = `${i}-${manifest?.key}`;

            if (manifest) {
              return (
                <ListBoxItem id={manifest?.key} key={key} data-test-id="warehouse-data-source">
                  <img
                    src={manifest?.icons?.square}
                    alt={`${manifest?.name} integration logo`}
                    className={styles.integrationIcon}
                  />
                  Warehouse native ({manifest?.warehouseVendor})
                </ListBoxItem>
              );
            } else {
              return (
                <ListBoxItem className={styles.skeletonItem} key={key} data-test-id="warehouse-data-source" isDisabled>
                  <span className={clsx(skeletonStyles.skeleton, styles.integrationIcon, styles.rounded)}></span>
                  <span className={clsx(skeletonStyles.skeleton, styles.skeletonText)}>Warehouse native</span>
                </ListBoxItem>
              );
            }
          })}
          {enableTraceBasedMetrics() && (
            <ListBoxItem id={TRACES_SELECTION} data-test-id="traces-data-source">
              <Icon name="pulse" size="small" />
              Traces
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </Select>
  );
};
